/**
 * Final - Special Purchase Conditions (HARDENED + Accelerated Checkout Support)
 */
(function () {
  const debounce = (fn, ms) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  let syncing = false;
  let pendingSync = false;

  /* =====================
   * DATA LAYER
   * ===================== */

  const fetchCart = async () => {
    const res = await fetch(`${theme.routes.cart_url}.js`);
    return res.json();
  };

  const updateCart = async (updates) => {
    await fetch(`${theme.routes.cart_update_url}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ updates }),
    });
  };

  /* =====================
   * RULES LAYER
   * ===================== */

  const getRulesFromDom = () => {
    const cartPage = document.getElementById("Cart");
    const cartDrawer = document.getElementById("Cart-Drawer");

    let el = cartPage?.querySelector(".js-special-purchase-rules");
    if (!el && cartDrawer) {
      el = cartDrawer.querySelector(".js-special-purchase-rules");
    }
    if (!el) {
      const elements = document.querySelectorAll(".js-special-purchase-rules");
      el = elements[elements.length - 1];
    }
    if (!el) return [];

    try {
      const parsed = JSON.parse((el.textContent || "").trim());
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((r) => {
          const pid = Number(r?.productId);
          if (!Number.isFinite(pid)) return null;

          return {
            productId: pid,
            maximumItems: Number(r.maximumItems) || 999999,
            soloOnly: Boolean(r.soloOnly),
            cartMessage: r.cartMessage || "",
          };
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  };

  const normalizeRules = (rules) => {
    const maxByProduct = {};
    const soloProducts = {};

    rules.forEach((r) => {
      const pid = r.productId;

      if (r.maximumItems && r.maximumItems < 999999) {
        if (!maxByProduct[pid] || r.maximumItems < maxByProduct[pid]) {
          maxByProduct[pid] = r.maximumItems;
        }
      }

      if (r.soloOnly) {
        soloProducts[pid] = r.cartMessage;
      }
    });

    return { maxByProduct, soloProducts };
  };

  const groupCartByProduct = (cart) => {
    const map = {};

    cart.items.forEach((item, index) => {
      const pid = Number(item.product_id);
      if (!map[pid]) map[pid] = [];

      map[pid].push({
        ...item,
        line: index + 1,
      });
    });

    return map;
  };

  const evaluateCart = (cart, normalizedRules) => {
    const { maxByProduct, soloProducts } = normalizedRules;
    const grouped = groupCartByProduct(cart);

    const updates = {};
    let blocked = false;
    const messages = new Set();

    // Max quantity rules
    Object.keys(maxByProduct).forEach((pidStr) => {
      const pid = Number(pidStr);
      const max = maxByProduct[pid];
      const items = grouped[pid] || [];

      const total = items.reduce((s, i) => s + i.quantity, 0);
      if (total <= max) return;

      let remaining = max;

      items.forEach((item) => {
        const newQty = Math.min(item.quantity, remaining);
        remaining -= newQty;
        updates[item.line] = newQty;
      });
    });

    // Solo rules
    Object.keys(soloProducts).forEach((pidStr) => {
      const pid = Number(pidStr);
      const hasProduct = !!grouped[pid];

      if (!hasProduct) return;

      const hasOther = Object.keys(grouped).some((otherPid) => Number(otherPid) !== pid);

      if (hasOther) {
        blocked = true;
        if (soloProducts[pid]) messages.add(soloProducts[pid]);
      }
    });

    return {
      updates,
      needsUpdate: Object.keys(updates).length > 0,
      blocked,
      messages: Array.from(messages),
      grouped,
    };
  };

  /* =====================
   * UI LAYER
   * ===================== */

  const resetQuantityInputs = () => {
    document.querySelectorAll("quantity-selector .qty").forEach((inp) => {
      if (inp.dataset.originalMax !== undefined) {
        const val = inp.dataset.originalMax;
        val ? inp.setAttribute("max", val) : inp.removeAttribute("max");
        delete inp.dataset.originalMax;
      }
    });
  };

  const applyQuantityUI = (cartState, normalizedRules) => {
    const { maxByProduct } = normalizedRules;

    Object.keys(maxByProduct).forEach((pidStr) => {
      const pid = Number(pidStr);
      const max = maxByProduct[pid];
      const items = cartState.grouped[pid] || [];

      const total = items.reduce((s, i) => s + i.quantity, 0);

      items.forEach((item) => {
        const rowIds = [`CartItem-${item.line}`, `CartDrawerItem-${item.line}`];

        rowIds.forEach((id) => {
          const row = document.getElementById(id);
          if (!row) return;

          row.querySelectorAll("quantity-selector").forEach((qs) => {
            const inp = qs.querySelector(".qty");
            if (!inp) return;

            if (inp.dataset.originalMax === undefined) {
              inp.dataset.originalMax = inp.getAttribute("max") || "";
            }

            const remaining = Math.max(0, max - total);
            const newMax = item.quantity + remaining;

            inp.setAttribute("max", newMax);

            qs.validateQtyRules?.();
          });
        });
      });
    });
  };

  const renderUI = (state) => {
    const text = state.messages.join(" ");

    document.querySelectorAll(".js-special-purchase-message").forEach((el) => {
      const textEl = el.querySelector(".js-special-purchase-message-text");

      if (state.blocked && text) {
        if (textEl) textEl.textContent = text;
        el.hidden = false;
      } else {
        if (textEl) textEl.textContent = "";
        el.hidden = true;
      }
    });

    // Normal checkout buttons
    document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      btn.disabled = state.blocked;
      btn.setAttribute("aria-disabled", state.blocked);
    });

    // Container block (handles accelerated checkout)
    document.querySelectorAll(".proceed-to-checkout").forEach((el) => {
      if (state.blocked) {
        el.classList.add("special-purchase-blocked");
      } else {
        el.classList.remove("special-purchase-blocked");
      }
    });
  };

  const resetUI = () => {
    renderUI({ blocked: false, messages: [] });
    resetQuantityInputs();
  };

  /* =====================
   * MAIN FLOW
   * ===================== */

  const sync = async () => {
    if (syncing) {
      pendingSync = true;
      return;
    }
    syncing = true;

    try {
      const rawRules = getRulesFromDom();
      if (!rawRules.length) return resetUI();

      let cart = await fetchCart();
      if (!cart.item_count) return resetUI();

      const cartProductIds = new Set(cart.items.map(i => Number(i.product_id)));
      const filteredRules = rawRules.filter(r => cartProductIds.has(r.productId));

      const rules = normalizeRules(filteredRules);

      let state = evaluateCart(cart, rules);

      if (state.needsUpdate) {
        await updateCart(state.updates);
        cart = await fetchCart();
        state = evaluateCart(cart, rules);
      }

      renderUI(state);

      requestAnimationFrame(() => {
        resetQuantityInputs();
        applyQuantityUI(state, rules);
      });
    } finally {
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        sync();
      }
    }
  };

  /* =====================
   * EVENTS
   * ===================== */

  const debouncedSync = debounce(sync, 80);

  document.addEventListener("cart:item-added", debouncedSync);
  document.addEventListener("line-item:change:end", debouncedSync);
  document.addEventListener("cart:refresh", debouncedSync);

  // Block ALL clicks inside blocked checkout area (accelerated included)
  document.body.addEventListener(
    "click",
    (e) => {
      if (e.target.closest(".special-purchase-blocked")) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true
  );

  // Block form submits
  document.body.addEventListener(
    "submit",
    (e) => {
      if (e.target.closest(".special-purchase-blocked")) {
        e.preventDefault();
      }
    },
    true
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sync);
  } else {
    sync();
  }
})();